import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { findNavKey } from './navItems';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import NotificationBell from '@/components/portal/NotificationBell';
import FeedbackWidget from '@/components/feedback/FeedbackWidget';
import { initials, useAuth } from '@/lib/auth';
import { catalogApi, type CatalogRow } from '@/lib/catalog';
import { specsApi } from '@/lib/specs';
import { usePerms } from '@/lib/perms';

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { me } = useAuth();
  const pageKey = findNavKey(location.pathname);

  useEffect(() => {
    setMobileOpen(false);
    setQuery('');
  }, [location.pathname]);

  // живой поиск по каталогу (этап 1.3): SKU, название, артикул поставщика — через /catalog?query=
  const [hits, setHits] = useState<CatalogRow[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      return;
    }
    let alive = true;
    const h = setTimeout(() => {
      catalogApi
        .list({ query: q, sort: 'stock', limit: 6 })
        .then((r) => alive && setHits(r))
        .catch(() => alive && setHits([]));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(h);
    };
  }, [query]);
  const results = useMemo(() => {
    const seen = new Set<string>();
    const productHits = hits.slice(0, 4).map((p) => ({ type: t('common.items'), label: `${p.sku} · ${p.name}`, to: `/catalog/${encodeURIComponent(p.sku)}`, icon: 'ri-armchair-line' }));
    const partnerHits = hits
      .filter((p) => p.partnerId && p.partnerName && !seen.has(p.partnerId) && seen.add(p.partnerId))
      .slice(0, 2)
      .map((p) => ({ type: t('nav.partners'), label: p.partnerName as string, to: `/partners/${p.partnerId}`, icon: 'ri-store-2-line' }));
    return [...productHits, ...partnerHits];
  }, [hits, t]);
  const { can } = usePerms();
  const [creating, setCreating] = useState(false);
  const newSpec = async () => {
    setCreating(true);
    try {
      const s = await specsApi.create({});
      navigate(`/specifications/${s.id}`);
    } catch {
      navigate('/specifications');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-100 text-foreground-900">
      <div className="fixed inset-y-0 left-0 z-30 hidden lg:block">
        <Sidebar />
      </div>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-foreground-950/30 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-y-0 left-0 z-50 bg-background-50 lg:hidden">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </>
      )}

      <div className="flex min-h-screen flex-col lg:pl-[248px]">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-4 border-b border-background-200 bg-background-50/95 px-4 backdrop-blur md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              aria-label={t('shell.openNav')}
              onClick={() => setMobileOpen(true)}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 lg:hidden"
            >
              <i className="ri-menu-line text-[16px]" />
            </button>
            <div className="flex min-w-0 items-center gap-2 text-[12px]">
              <span className="hidden text-foreground-400 sm:inline">{t('shell.hub')}</span>
              <i className="ri-arrow-right-s-line hidden text-[14px] text-foreground-300 sm:inline" />
              <span className="truncate font-semibold text-foreground-900">{t(pageKey)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative hidden md:block">
              <i className="ri-search-line pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-foreground-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                placeholder={t('shell.searchPlaceholder')}
                aria-label={t('shell.searchPlaceholder')}
                className="h-8 w-[248px] rounded-md border border-background-200 bg-background-100 pl-8 pr-2 text-[12px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
              />
              {searchFocused && results.length > 0 && (
                <div className="absolute left-0 top-10 w-[340px] overflow-hidden rounded-md border border-background-200 bg-background-50 py-1">
                  {results.map((r) => (
                    <button
                      key={`${r.type}-${r.to}`}
                      type="button"
                      onMouseDown={() => navigate(r.to)}
                      className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left hover:bg-background-100"
                    >
                      <i className={`${r.icon} text-[13px] text-foreground-400`} />
                      <span className="flex-1 truncate text-[12px] text-foreground-800">{r.label}</span>
                      <span className="text-[10px] uppercase tracking-wide text-foreground-400">{r.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <LanguageSwitcher />

            {can('specifications', 'write') && (
              <button
                type="button"
                disabled={creating}
                onClick={() => void newSpec()}
                className="hidden h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-2.5 text-[12px] font-medium text-foreground-600 transition-colors hover:bg-background-100 disabled:opacity-50 xl:flex"
              >
                <i className="ri-add-line text-[13px]" />
                {t('shell.newSpec')}
              </button>
            )}

            <NotificationBell settingsPath="/settings?tab=users" allPath="/settings?tab=feedback" />

            <Link
              to="/settings"
              className="flex items-center gap-2 rounded-md border border-background-200 py-1 pl-1 pr-2.5 transition-colors hover:bg-background-100"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded bg-primary-500 text-[10px] font-semibold text-background-50">
                {initials(me?.name ?? '')}
              </span>
              <span className="hidden max-w-[140px] truncate text-[11px] font-medium text-foreground-800 sm:inline">{me?.name ?? t('shell.admin')}</span>
              <i className="ri-arrow-down-s-line text-[12px] text-foreground-400" />
            </Link>
          </div>
        </header>

        <main className="flex-1 px-4 py-5 md:px-6 md:py-6">
          <div className="fade-in">
            <Outlet />
          </div>
        </main>

        <footer className="flex flex-col items-start justify-between gap-1 border-t border-background-200 px-4 py-3 text-[11px] text-foreground-400 sm:flex-row sm:items-center md:px-6">
          <p>{t('shell.footer')}</p>
          <p className="tnum">{t('shell.prices')}</p>
        </footer>
      </div>

      <FeedbackWidget screen={t(pageKey)} listPath="/settings?tab=feedback" />
    </div>
  );
}